import { type Layer, type PixelCanvasRef, type PixelOp, Tool } from "#canvas/types.ts";
import { AIAssistant } from "#components/AIAssistant.tsx";
import { CanvasArea } from "#components/CanvasArea.tsx";
import { LayersPanel } from "#components/LayersPanel.tsx";
import { MachineEditor } from "#components/MachineEditor/index.tsx";
import { Timeline } from "#components/Timeline.tsx";
import { Toolsbar } from "#components/Toolsbar.tsx";
import { Topbar } from "#components/Topbar.tsx";
import { buildUserMachine } from "#machines/build.ts";
import { playbackMachine, type PlaybackMachineActor } from "#machines/playback.ts";
import { STARTER_MACHINE } from "#machines/starter.ts";
import { decode, encode, isPxpf, type Pxpf } from "#puf/codec.ts";
import { COLS, ROWS } from "#puf/constants.ts";
import { blendWithBg, downloadDataURL, emptyData, emptyFrameLayers, makeSpritesheet, makeThumb } from "#puf/helpers.ts";
import { fetchAssistantEnabled } from "#puf/lib/runtime-config.ts";
import { frameRange, type StateFrameRanges } from "#puf/machine.ts";
import { store } from "#puf/store/index.ts";
import { selectFps } from "#puf/store/selectors.ts";
import { useActorRef, useSelector as useActorSelector } from "@xstate/react";
import { useSelector } from "@xstate/store-react";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

export default function App() {
  if (import.meta.env.DEV) console.count("App render");
  const [assistantEnabled, setAssistantEnabled] = useState(false);
  const [assistantOpen, setAssistantOpen] = useState(false);
  const ref = useRef<PixelCanvasRef | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const framesRef = useRef<Layer[][]>([]);

  // store reads
  const stateMachine = useSelector(store, (s) => s.context.stateMachine);
  const tab = useSelector(store, (s) => s.context.tab);
  const mode = useSelector(store, (s) => s.context.mode);
  const frames = useSelector(store, (s) => s.context.frames);
  const activeFrame = useSelector(store, (s) => s.context.activeFrame);
  const activeLayerId = useSelector(store, (s) => s.context.activeLayerId);
  const onion = useSelector(store, (s) => s.context.onion);
  const onionOpacity = useSelector(store, (s) => s.context.onionOpacity);
  const fps = useSelector(store, (s) => s.context.fps);
  const projectName = useSelector(store, (s) => s.context.projectName);

  // mirror frames into ref so canvas-sync effect doesn't re-fire on snapshots
  useEffect(() => {
    framesRef.current = frames;
  }, [frames]);

  useEffect(() => {
    let active = true;
    void fetchAssistantEnabled().then((enabled) => {
      if (!active) return;
      setAssistantEnabled(enabled);
      if (!enabled) setAssistantOpen(false);
    });
    return () => {
      active = false;
    };
  }, []);

  // canvas commit handler — write back to active frame + thumb
  const onCanvasCommit = useCallback((layers: Layer[]) => {
    store.trigger["frame.update"]({ index: activeFrame, layers });
    store.trigger["thumb.update"]({ index: activeFrame, url: makeThumb(layers) });
  }, [activeFrame]);

  // picker handler — set color, drop back to DRAW
  const onPick = useCallback((picked: string) => {
    store.trigger["color.set"]({ color: picked });
    store.trigger["mode.set"]({ mode: { kind: "brush", brush: Tool.DRAW } });
  }, []);

  // canvas follows activeFrame — push new frame's layers to canvas explicitly so
  // its history captures a snapshot per frame switch.
  useEffect(() => {
    const target = framesRef.current[activeFrame];
    if (target) ref.current?.setLayers(target);
  }, [activeFrame]);

  // onion skin
  useEffect(() => {
    if (!onion || activeFrame === 0) {
      ref.current?.setIndicators([]);
      return;
    }
    const prev = frames[activeFrame - 1];
    const curr = frames[activeFrame];
    if (!prev || !curr) return;
    const occupied = new Set<string>();
    for (const layer of curr) {
      for (let r = 0; r < layer.data.length; r++) {
        const row = layer.data[r];
        if (!row) continue;
        for (let c = 0; c < row.length; c++) {
          if (row[c]) occupied.add(`${r}|${c}`);
        }
      }
    }
    const indicators: PixelOp[] = [];
    for (const layer of prev) {
      for (let r = 0; r < layer.data.length; r++) {
        const row = layer.data[r];
        if (!row) continue;
        for (let c = 0; c < row.length; c++) {
          const col = row[c];
          if (!col) continue;
          const key = `${r}|${c}`;
          if (occupied.has(key)) continue;
          indicators.push({ row: r, col: c, color: blendWithBg(col, onionOpacity) });
        }
      }
    }
    ref.current?.setIndicators(indicators);
  }, [activeFrame, onion, onionOpacity, frames]);

  // ─── user machine + playback wiring ──────────────────────────────────────
  // Build the user-defined machine from the persisted `stateMachine` config.
  // Falls back to the starter machine when no config exists yet (fresh project).
  // Rebuilt only when the config changes — the actor below restarts in lockstep.
  const userMachine = useMemo(() => buildUserMachine(stateMachine ?? STARTER_MACHINE), [stateMachine]);
  const machineActor = useActorRef(userMachine);
  const currentStateId = useActorSelector(machineActor, (s) => String(s.value));

  // Stash the live machine actor in a ref so the playback machine's `advance`
  // action can read it via the `getMachineActor` input getter — this sidesteps
  // useActorRef's mount-time input capture: when the user edits their machine,
  // `machineActor` changes, the ref points at the new actor, and the next tick
  // sees the updated actor.
  const machineActorRef = useRef<PlaybackMachineActor | null>(machineActor);
  useEffect(() => {
    machineActorRef.current = machineActor;
  }, [machineActor]);

  const playbackActor = useActorRef(playbackMachine, {
    input: { store, getMachineActor: () => machineActorRef.current },
  });
  // Scoped subscription — only re-render App when play/pause flips, not per tick.
  const playing = useActorSelector(playbackActor, (s) => s.matches("playing"));

  // fps change → re-enter `playing` so the running `after` rebuilds with the
  // new delay. Subscribe to the store-level `selectFps` instead of a useEffect
  // dep so the listener reaches outside React's render cycle.
  useEffect(() => {
    const sub = selectFps.subscribe(() => {
      playbackActor.send({ type: "FPS_CHANGED" });
    });
    return () => {
      sub.unsubscribe();
    };
  }, [playbackActor]);

  // Mirror `playing` into a ref so the state-jump effect can gate without
  // landing `playing` in its dep array (which would re-fire on pause and
  // wrongly snap activeFrame back to range[0]).
  const playingRef = useRef(playing);
  useLayoutEffect(() => {
    playingRef.current = playing;
  }, [playing]);

  // user-machine state change → jump to range start. Gated so playback-driven
  // RANGE_END transitions (which already wrote the new range's start in the
  // playback action) skip this; only externally-driven flips reach the snap.
  useEffect(() => {
    if (playingRef.current) return;
    const range = store.getSnapshot().context.stateFrames[currentStateId];
    if (range) store.trigger["activeFrame.set"]({ index: range[0] });
  }, [currentStateId]);

  const togglePlayback = useCallback(() => {
    playbackActor.send({ type: "TOGGLE" });
  }, [playbackActor]);

  // ─── layer ops on the active frame ───
  const activeFrameLayers = useMemo(() => frames[activeFrame] ?? emptyFrameLayers(), [frames, activeFrame]);

  const onAddLayer = useCallback(() => {
    const newId = `L${Date.now().toString(36)}`;
    const next: Layer[] = [
      ...activeFrameLayers,
      { id: newId, visible: true, data: emptyData() },
    ];
    store.trigger["frame.update"]({ index: activeFrame, layers: next });
    store.trigger["activeLayerId.set"]({ id: newId });
  }, [activeFrameLayers, activeFrame]);

  const onSelectLayer = useCallback((id: string) => {
    store.trigger["activeLayerId.set"]({ id });
  }, []);

  const onToggleVisibility = useCallback((id: string, currentlyVisible: boolean) => {
    const next = activeFrameLayers.map((l) => (l.id === id ? { ...l, visible: !currentlyVisible } : l));
    store.trigger["frame.update"]({ index: activeFrame, layers: next });
  }, [activeFrameLayers, activeFrame]);

  const onDeleteLayer = useCallback((id: string) => {
    if (activeFrameLayers.length <= 1) return;
    const next = activeFrameLayers.filter((l) => l.id !== id);
    store.trigger["frame.update"]({ index: activeFrame, layers: next });
    if (activeLayerId === id) {
      const fallback = next[0]?.id ?? "L1";
      store.trigger["activeLayerId.set"]({ id: fallback });
    }
  }, [activeFrameLayers, activeFrame, activeLayerId]);

  // ─── topbar callbacks ───
  const onUndo = useCallback(() => ref.current?.undo(), []);
  const onRedo = useCallback(() => ref.current?.redo(), []);

  const onExportPng = useCallback(() => {
    const url = ref.current?.exportPng();
    if (url) downloadDataURL(url, `${projectName || "untitled"}.png`);
  }, [projectName]);

  const onExportSheet = useCallback(() => {
    const url = makeSpritesheet(frames);
    if (url) downloadDataURL(url, "pixelpuf-spritesheet.png");
  }, [frames]);

  const applyPxpf = useCallback((pxpf: Pxpf) => {
    const decoded = decode(pxpf);
    if (decoded.rows !== ROWS || decoded.cols !== COLS) {
      console.warn(
        `pxpf canvas ${decoded.cols}x${decoded.rows} doesn't match runtime ${COLS}x${ROWS}; skipping load`,
      );
      return;
    }

    const restored: Layer[][] = decoded.frames.map((frame) => frame.length > 0 ? frame : emptyFrameLayers());
    if (restored.length === 0) restored.push(emptyFrameLayers());

    // Re-brand stateFrames after JSON round-trip — FrameRange's symbol-brand
    // erases on JSON parse, so re-validate via frameRange() which guards
    // start <= end / non-negative ints.
    const restoredRanges: StateFrameRanges = {};
    for (const [id, range] of Object.entries(decoded.stateFrames)) {
      if (!range) continue;
      restoredRanges[id] = frameRange(range[0], range[1]);
    }

    store.trigger["project.load"]({
      payload: {
        frames: restored,
        fps: decoded.fps,
        projectName: decoded.name,
        stateFrames: restoredRanges,
      },
    });
  }, []);

  const onNewProject = useCallback(() => {
    store.trigger.reset();
  }, []);

  const onSaveFile = useCallback(() => {
    const ctx = store.getSnapshot().context;
    const pxpf = encode({
      name: projectName,
      rows: ROWS,
      cols: COLS,
      fps,
      frames,
      stateFrames: ctx.stateFrames,
      stateMachine: ctx.stateMachine,
      machineLayout: ctx.machineLayout,
    });
    const blob = new Blob([JSON.stringify(pxpf)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    downloadDataURL(url, `${projectName || "untitled"}.pxpf`);
    setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 0);
  }, [projectName, fps, frames]);

  const onLoadFile = useCallback(() => fileInputRef.current?.click(), []);

  const onFilePicked = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    void file.text().then((txt) => {
      try {
        const parsed: unknown = JSON.parse(txt);
        if (!isPxpf(parsed)) {
          console.warn("invalid .pxpf file");
          return;
        }
        applyPxpf(parsed);
      } catch (err) {
        console.warn("failed to parse .pxpf", err);
      }
    });
  }, [applyPxpf]);

  // NOTE: autosave + mount-restore previously lived here as two effects writing
  // to / reading from `localStorage[AUTOSAVE_KEY]` via `packAutosave`/`unpackAutosave`.
  // Both are now handled by `@xstate/store/persist` configured in `src/store/index.ts`,
  // which writes throttled snapshots to `localStorage["pixelpuf:store"]` and
  // rehydrates synchronously on store creation.

  const layerInfos = useMemo(
    () => activeFrameLayers.map((l) => ({ id: l.id, visible: l.visible })),
    [activeFrameLayers],
  );

  // mode-aware: if picker is on, callback is set; otherwise undefined inside CanvasArea
  void mode;

  return (
    <div className="grid grid-cols-1 grid-rows-[auto_1fr] min-h-dvh lg:h-dvh lg:overflow-hidden">
      <Topbar
        assistantEnabled={assistantEnabled}
        assistantOpen={assistantOpen}
        onNew={onNewProject}
        onSave={onSaveFile}
        onLoad={onLoadFile}
        onToggleAssistant={() => {
          setAssistantOpen((open) => !open);
        }}
        onUndo={onUndo}
        onRedo={onRedo}
        onExportPng={onExportPng}
        onExportSheet={onExportSheet}
      />
      <input
        ref={fileInputRef}
        type="file"
        name="pxpf-import"
        accept=".pxpf, application/json"
        onChange={onFilePicked}
        className="hidden"
      />

      <div className="flex flex-col min-w-0 lg:grid lg:grid-cols-[240px_1fr_280px] lg:grid-rows-[1fr_220px] lg:min-h-0">
        {tab === "canvas"
          ? (
            <>
              <CanvasArea ref={ref} onCommit={onCanvasCommit} onPick={onPick} />
              <Toolsbar />
              <Timeline playing={playing} togglePlaying={togglePlayback} />
              <LayersPanel
                layers={layerInfos}
                currentLayerId={activeLayerId}
                onSelect={onSelectLayer}
                onToggleVisibility={onToggleVisibility}
                onDelete={onDeleteLayer}
                onAddLayer={onAddLayer}
              />
            </>
          )
          : (
            <div className="flex-1 min-h-[60dvh] lg:min-h-0 lg:col-span-full lg:row-span-full">
              <MachineEditor />
            </div>
          )}
      </div>
      {assistantEnabled && (
        <AIAssistant
          onClose={() => {
            setAssistantOpen(false);
          }}
          open={assistantOpen}
        />
      )}
    </div>
  );
}
